import {
  Button,
  Inline,
  Modal,
  type ModalProps,
  Notification,
  notifySuccess,
  Stack,
} from '@omnipresentgroup/design-system';
import { useContractorContractMutation } from '@omnipresentgroup/domains';
import { Form } from '@omnipresentgroup/flexible-forms';
import type {
  Contract,
  InvoicingCurrencyEnum,
  InvoicingFrequencyEnum,
} from '@omnipresentgroup/types';
import type { IChangeEvent } from '@rjsf/core';
import { useEffect, useState } from 'react';

import { useEditContractingDetailsSchema } from './hooks';

const FORM_ID = 'edit-contracting-details-form';
const MODAL_TITLE = 'Edit contracting details';
const NOTIFICATION_TITLE =
  'Changing these fields does not impact the activation, deactivation or fee calculation. ';
const NOTIFICATION_DESC =
  'To deactivate a contractor, please click Deactivate.';

type Props = {
  modalProps: Omit<ModalProps, 'children'>;
  handleCancel: () => void;
  handleConfirm: () => void;
  contract: Contract;
};

export type EditContractingDetailsFormFields = {
  invoicingAmount: {
    currency: InvoicingCurrencyEnum;
    amount?: number;
  };
  frequency?: InvoicingFrequencyEnum;
  dateRange?: {
    startDate?: string;
    endDate?: string;
  };
};

export const ContractorContractingDetailsEditModal = ({
  modalProps,
  handleCancel,
  handleConfirm,
  contract,
}: Props) => {
  const [formValues, setFormValues] = useState<
    EditContractingDetailsFormFields | undefined
  >({
    invoicingAmount: {
      currency: contract.invoicingCurrency,
      amount: contract.rate === null ? undefined : Number(contract.rate),
    },
    frequency:
      contract.expectedInvoicingFrequency === null
        ? undefined
        : contract.expectedInvoicingFrequency,
    dateRange: {
      startDate: contract.startDate === null ? undefined : contract.startDate,
      endDate: contract.endDate === null ? undefined : contract.endDate,
    },
  });

  const {
    mutate: updateContract,
    isPending: isUpdatingContract,
    isSuccess: isSuccessUpdatingContract,
  } = useContractorContractMutation({
    contractorId: contract.contractorId,
    contractId: contract.id,
  });

  const { schema: flexibleSchema, uiSchema: flexibleUiSchema } =
    useEditContractingDetailsSchema({
      formValues,
    });

  // Form Change
  const handleChange = ({
    formData,
  }: IChangeEvent<EditContractingDetailsFormFields>) => {
    setFormValues(formData);
  };

  // Form Submit
  const handleSubmit = () => {
    if (formValues) {
      updateContract({
        rate: formValues.invoicingAmount.amount?.toString(),
        expectedInvoicingFrequency: formValues.frequency,
        startDate: formValues.dateRange?.startDate,
        endDate: formValues.dateRange?.endDate,
      });
    }
  };

  // Handle Update Success
  useEffect(() => {
    if (isSuccessUpdatingContract) {
      handleConfirm?.();
      notifySuccess({ title: 'Contracting details updated successfully' });
    }
  }, [handleConfirm, isSuccessUpdatingContract]);

  return (
    <Modal {...modalProps} span={4}>
      <Modal.Header title={MODAL_TITLE} />
      <Modal.Body>
        <Stack gap="24">
          <Notification
            id="edit-contracting-details-notification"
            title={NOTIFICATION_TITLE}
            description={NOTIFICATION_DESC}
          />
          <Form<EditContractingDetailsFormFields>
            id={FORM_ID}
            schema={flexibleSchema}
            uiSchema={flexibleUiSchema}
            formData={formValues}
            onChange={handleChange}
            onSubmit={handleSubmit}
          />
        </Stack>
      </Modal.Body>
      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isUpdatingContract}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            form={FORM_ID}
            loading={isUpdatingContract}
          >
            Update
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
